import { Link } from "react-router-dom";
import { X, Minus, Plus } from "lucide-react";
import { useCart } from "../context/CartContext";
import { formatPrice } from "../lib/api";

export default function CartSheet() {
  const { items, open, setOpen, updateQty, remove, subtotal, clear } = useCart();

  return (
    <>
      {open && (
        <div
          data-testid="cart-overlay"
          onClick={() => setOpen(false)}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity"
        />
      )}
      <aside
        data-testid="cart-sheet"
        className={`fixed top-0 right-0 h-full w-full sm:w-[440px] bg-[#0F120E] border-l border-[#2E3628] z-50 transform transition-transform duration-500 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-8 py-6 border-b border-[#2E3628]">
          <div>
            <div className="overline">Your selection</div>
            <div className="font-display text-2xl text-[#F9F6F0] mt-1">Cart</div>
          </div>
          <button
            data-testid="cart-close-btn"
            onClick={() => setOpen(false)}
            className="text-[#9AA894] hover:text-[#F9F6F0]"
          >
            <X className="w-5 h-5" strokeWidth={1.2} />
          </button>
        </div>

        <div className="overflow-y-auto h-[calc(100%-220px)] px-8 py-6">
          {items.length === 0 && (
            <div data-testid="cart-empty" className="text-center pt-20">
              <p className="text-[#9AA894] text-sm leading-relaxed">
                Your cart is gently empty.
                <br />
                Begin with a dish from our seasonal menu.
              </p>
              <Link
                to="/order"
                onClick={() => setOpen(false)}
                data-testid="cart-empty-cta"
                className="btn-line mt-8 inline-flex"
              >
                Explore menu
              </Link>
            </div>
          )}

          {items.map((i) => (
            <div
              key={i.menu_item_id}
              data-testid={`cart-item-${i.menu_item_id}`}
              className="flex items-start justify-between py-5 border-b border-[#2E3628]/60"
            >
              <div className="flex-1 pr-4">
                <div className="text-[#F9F6F0] font-display text-lg leading-tight">{i.name}</div>
                <div className="text-[#9AA894] text-xs mt-1">{formatPrice(i.price)} each</div>
                <div className="flex items-center gap-3 mt-3">
                  <button
                    data-testid={`cart-decrease-${i.menu_item_id}`}
                    onClick={() => updateQty(i.menu_item_id, -1)}
                    className="border border-[#2E3628] w-7 h-7 flex items-center justify-center text-[#F9F6F0] hover:border-[#D4C1A0]"
                  >
                    <Minus className="w-3 h-3" strokeWidth={1.2} />
                  </button>
                  <span className="text-[#F9F6F0] text-sm w-6 text-center">{i.quantity}</span>
                  <button
                    data-testid={`cart-increase-${i.menu_item_id}`}
                    onClick={() => updateQty(i.menu_item_id, 1)}
                    className="border border-[#2E3628] w-7 h-7 flex items-center justify-center text-[#F9F6F0] hover:border-[#D4C1A0]"
                  >
                    <Plus className="w-3 h-3" strokeWidth={1.2} />
                  </button>
                  <button
                    data-testid={`cart-remove-${i.menu_item_id}`}
                    onClick={() => remove(i.menu_item_id)}
                    className="ml-auto text-[10px] uppercase tracking-widest text-[#9AA894] hover:text-[#D4C1A0]"
                  >
                    Remove
                  </button>
                </div>
              </div>
              <div className="text-[#D4C1A0] font-display text-lg">
                {formatPrice(i.price * i.quantity)}
              </div>
            </div>
          ))}
        </div>

        {items.length > 0 && (
          <div className="absolute bottom-0 left-0 right-0 border-t border-[#2E3628] bg-[#0F120E] px-8 py-6">
            <div className="flex justify-between mb-4">
              <span className="overline">Subtotal</span>
              <span data-testid="cart-subtotal" className="font-display text-2xl text-[#F9F6F0]">
                {formatPrice(subtotal)}
              </span>
            </div>
            <Link
              to="/order"
              onClick={() => setOpen(false)}
              data-testid="cart-checkout-btn"
              className="btn-solid w-full text-center justify-center inline-flex"
            >
              Checkout
            </Link>
            <button
              data-testid="cart-clear-btn"
              onClick={clear}
              className="block mx-auto mt-3 text-[10px] uppercase tracking-widest text-[#9AA894] hover:text-[#D4C1A0]"
            >
              Clear cart
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
